import { useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { countries, allProjects } from "../data/projects";
import { MapPin, Calendar, Briefcase, Building2, Gauge } from "lucide-react";

export default function ProjectDrawer({ countryIso, open, onOpenChange, focusedProjectId }) {
  const country = countryIso ? countries[countryIso] : null;
  const projects = countryIso ? allProjects.filter((p) => p.country === countryIso) : [];
  const scrollRef = useRef(null);

  // If there's a focused project, reorder so it's at the top — avoids needing offset math.
  const ordered = focusedProjectId
    ? [
        ...projects.filter((p) => p.id === focusedProjectId),
        ...projects.filter((p) => p.id !== focusedProjectId),
      ]
    : projects;

  useEffect(() => {
    if (!open || !scrollRef.current) return;
    // always start from top of the project list when drawer opens
    scrollRef.current.scrollTop = 0;
  }, [open, focusedProjectId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="project-drawer"
        className="w-full sm:max-w-lg md:max-w-xl p-0 border-0 flex flex-col"
        style={{ backgroundColor: "var(--bg)", color: "var(--text-primary)" }}
      >
        <div className="shrink-0 px-8 pt-10 pb-5" style={{ backgroundColor: "var(--bg)", borderBottom: "1px solid var(--border-soft)" }}>
          <SheetHeader className="text-left space-y-2">
            <div className="overline">Country · Portfolio</div>
            <SheetTitle className="font-serif text-4xl tracking-tight" style={{ color: "var(--primary)" }}>
              {country?.name || ""}
            </SheetTitle>
            <SheetDescription className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {projects.length} project{projects.length !== 1 ? "s" : ""} delivered across design, due-diligence, modelling and asset management.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-8 space-y-5">
          {ordered.map((p) => {
            const isFocus = p.id === focusedProjectId;
            return (
              <article
                key={p.id}
                id={`project-${p.id}`}
                data-testid={`drawer-project-${p.id}`}
                className="p-6"
                style={{
                  border: isFocus ? "1px solid var(--accent)" : "1px solid var(--border-soft)",
                  backgroundColor: isFocus ? "var(--surface)" : "transparent",
                  borderLeft: isFocus ? "3px solid var(--accent)" : "1px solid var(--border-soft)",
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-serif text-xl leading-tight" style={{ color: "var(--primary)" }}>{p.name}</h3>
                  {p.flagship && (
                    <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-0.5" style={{ color: "var(--accent-strong)", border: "1px solid var(--accent-strong)" }}>
                      Flagship
                    </span>
                  )}
                </div>

                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{p.description}</p>

                <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                  <Row icon={<Gauge size={12} />} label="Capacity" value={p.capacity} />
                  <Row icon={<Calendar size={12} />} label="Year" value={p.year} />
                  <Row icon={<Briefcase size={12} />} label="Role" value={p.role} />
                  <Row icon={<Building2 size={12} />} label="Client" value={p.client} />
                  <Row icon={<MapPin size={12} />} label="Sector" value={p.sector} />
                </div>
              </article>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Row({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 font-mono uppercase tracking-widest font-semibold" style={{ color: "var(--text-secondary)", fontSize: "0.6rem" }}>
        {icon} {label}
      </div>
      <div className="mt-1" style={{ color: "var(--text-primary)" }}>{value || "—"}</div>
    </div>
  );
}
